import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Loader2, Gavel, FileDown, CheckCircle2, Award, Palette, Link2 } from "lucide-react";
import { exportDoc } from "../../pages/ExamSessionPage";
import DiplomaConfigDialog from "./DiplomaConfigDialog";

const DECISION_BADGE = {
  certifie: { label: "CERTIFIÉ", cls: "bg-emerald-500/15 text-emerald-300" },
  ajourne: { label: "AJOURNÉ", cls: "bg-rose-500/15 text-rose-300" },
  absent: { label: "ABSENT", cls: "bg-white/10 text-slate-400" },
};

async function downloadPdf(api, path, filename) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Diplôme indisponible (candidat non certifié ?)");
  }
}

export default function DeliberationTab({ api, session, cert, onChange }) {
  const [closing, setClosing] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const letters = (session.sujets || []).map(s => s.letter);
  const comps = cert.competences || [];
  const totalMax = comps.reduce((a, c) => a + (c.points_max || 0), 0);
  const nbCertifies = (session.candidates || []).filter(c => c.decision === "certifie").length;

  const close = async () => {
    setClosing(true);
    try {
      const { data } = await api.post(`/certif/sessions/${session.session_id}/deliberate`);
      let msg = `Session clôturée : ${data.certifies} certifié(s), ${data.ajournes} ajourné(s). Rattrapages jusqu'au ${data.deadline_rattrapage}.`;
      if (data.diplomes_email) {
        const de = data.diplomes_email;
        msg += de.sent > 0
          ? ` 🎓 Diplômes envoyés par email : ${de.sent}/${de.total}.`
          : ` Diplômes non envoyés par email (${de.results?.[0]?.reason || "destinataires sans email ou SMTP non configuré"}).`;
      }
      toast.success(msg, { duration: 8000 });
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setClosing(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <p className="text-sm font-bold text-white flex items-center gap-1.5"><Gavel className="w-4 h-4 text-[#f7c935]" /> Délibération du jury</p>
          <p className="text-[10px] text-slate-500">
            Certifié si QCM ≥ {cert.qcm?.seuil || 10}/{cert.qcm?.bareme || 20} ET pratique ≥ {cert.pratique?.seuil_points || 0}/{totalMax} pts. Les épreuves validées d'un candidat ajourné sont conservées 12 mois.
          </p>
        </div>
        <Button variant="outline" className="border-white/10 text-slate-300" onClick={() => exportDoc(api, session.session_id, "pv.docx")} data-testid="export-pv-btn">
          <FileDown className="w-4 h-4 mr-1" /> PV de délibération (DOCX)
        </Button>
        {nbCertifies > 0 && (
          <Button variant="outline" className="border-[#f7c935]/40 text-[#f7c935] hover:bg-[#f7c935]/10"
            onClick={() => downloadPdf(api, `/certif/sessions/${session.session_id}/diplomas.pdf`, `Diplomes_${cert.code || ""}.pdf`)}
            data-testid="export-all-diplomas-btn">
            <Award className="w-4 h-4 mr-1" /> Tous les diplômes (PDF) — {nbCertifies}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={() => setShowConfig(true)} data-testid="diploma-config-btn" title="Personnaliser le diplôme (couleurs, signataire)">
          <Palette className="w-4 h-4" />
        </Button>
        {session.statut !== "cloturee" && (
          <Button onClick={close} disabled={closing} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="deliberate-btn">
            {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Clôturer la session"}
          </Button>
        )}
      </div>

      {(session.candidates || []).length === 0 ? (
        <p className="text-center text-sm text-slate-500 py-8">Aucun candidat inscrit.</p>
      ) : (session.candidates || []).map(c => (
        <CandidateResultRow key={c.candidate_id} api={api} session={session} cert={cert}
          candidate={c} letters={letters} comps={comps} totalMax={totalMax} onChange={onChange} />
      ))}
      <DiplomaConfigDialog api={api} open={showConfig} onClose={() => setShowConfig(false)} />
    </div>
  );
}

function CandidateResultRow({ api, session, cert, candidate, letters, comps, totalMax, onChange }) {
  const res = candidate.resultats || {};
  const [qcmScore, setQcmScore] = useState(res.qcm_score ?? "");
  const [letter, setLetter] = useState(res.sujet_letter || "");
  const [grille, setGrille] = useState(res.grille || {});
  const [saving, setSaving] = useState(false);
  const badge = DECISION_BADGE[candidate.decision];
  const total = Object.values(grille).reduce((a, v) => a + (parseFloat(v) || 0), 0);

  const save = async (absent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/certif/sessions/${session.session_id}/results/${candidate.candidate_id}`,
        absent ? { absent: true } : { qcm_score: qcmScore === "" ? null : Number(qcmScore), sujet_letter: letter || null, grille });
      toast.success(absent ? "Marqué absent" : `Décision : ${(data.decision || "").toUpperCase()}`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`result-row-${candidate.candidate_id}`}>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <p className="text-sm font-bold text-white flex-1">{candidate.nom} {candidate.prenom}</p>
        {(candidate.epreuves_validees || []).length > 0 && candidate.decision !== "certifie" && (
          <span className="text-[10px] text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Conservé : {candidate.epreuves_validees.join(", ")}</span>
        )}
        {candidate.decision === "certifie" && (
          <Button size="sm" variant="outline" className="border-[#f7c935]/40 text-[#f7c935] hover:bg-[#f7c935]/10 text-xs h-7"
            onClick={() => downloadPdf(api, `/certif/sessions/${session.session_id}/diploma/${candidate.candidate_id}.pdf`, `Diplome_${candidate.nom}_${candidate.prenom}.pdf`)}
            data-testid={`diploma-btn-${candidate.candidate_id}`}>
            <Award className="w-3.5 h-3.5 mr-1" /> Diplôme (PDF)
          </Button>
        )}
        {session.statut === "cloturee" && candidate.portal_token && (
          <Button size="sm" variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5 text-xs h-7"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/resultat/${candidate.portal_token}`);
              toast.success("Lien résultat candidat copié dans le presse-papiers");
            }}
            title="Copier le lien sécurisé à transmettre au candidat"
            data-testid={`copy-portal-link-${candidate.candidate_id}`}>
            <Link2 className="w-3.5 h-3.5 mr-1" /> Lien résultat
          </Button>
        )}
        {badge && <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badge.cls}`} data-testid={`decision-${candidate.candidate_id}`}>{badge.label}</span>}
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="text-[10px] uppercase text-slate-500">QCM /{cert.qcm?.bareme || 20}</label>
          <Input type="number" step="0.5" className="bg-[#0b0811] border-white/10 text-white w-24" value={qcmScore}
            onChange={e => setQcmScore(e.target.value)} data-testid={`qcm-score-${candidate.candidate_id}`} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500">Sujet tiré</label>
          <select value={letter} onChange={e => setLetter(e.target.value)}
            className="block bg-[#0b0811] border border-white/10 rounded-lg text-sm text-white px-2 py-2 w-20"
            data-testid={`sujet-tire-${candidate.candidate_id}`}>
            <option value="">—</option>
            {letters.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        {comps.map(comp => (
          <div key={comp.code}>
            <label className="text-[10px] uppercase text-slate-500">{comp.code} /{comp.points_max}</label>
            <Input type="number" step="0.5" min="0" max={comp.points_max}
              className="bg-[#0b0811] border-white/10 text-white w-16"
              value={grille[comp.code] ?? ""}
              onChange={e => setGrille({ ...grille, [comp.code]: e.target.value })}
              data-testid={`grille-${comp.code}-${candidate.candidate_id}`} />
          </div>
        ))}
        <div className="text-xs text-white font-bold pb-2">Total pratique : {total}/{totalMax}</div>
        <Button size="sm" onClick={() => save(false)} disabled={saving || session.statut === "cloturee"} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs" data-testid={`save-results-${candidate.candidate_id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Enregistrer"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => save(true)} disabled={session.statut === "cloturee"} className="border-white/10 text-slate-400 text-xs">Absent</Button>
      </div>
    </div>
  );
}
